/**
 * Production-ready logging utility
 * Only logs errors in production, full logging in development
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';

  private log(level: LogLevel, message: string, data?: any): void {
    // In production, only log errors
    if (!this.isDevelopment && level !== LogLevel.ERROR) {
      return;
    }

    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] ${level.toUpperCase()}: ${message}`;

    switch (level) {
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          console.debug(logMessage, data || '');
        }
        break;
      case LogLevel.INFO:
        if (this.isDevelopment) {
          console.info(logMessage, data || '');
        }
        break;
      case LogLevel.WARN:
        if (this.isDevelopment) {
          console.warn(logMessage, data || '');
        }
        break;
      case LogLevel.ERROR:
        console.error(logMessage, data || '');
        // In production, you might want to send errors to a monitoring service
        // this.sendToMonitoringService(message, data);
        break;
    }
  }

  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }

  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }

  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, message, data);
  }

  // Logger utilities for API integration errors
  apiError(operation: string, error: any): void {
    this.error(`API Error in ${operation}`, {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url
    });
  }
}

export const logger = new Logger();